/**
 * DER BUEHNENBAU — eine Fassung fuer Browser UND Server.
 *
 * WARUM DIESE DATEI AUSSERHALB VON src/ UND server/ LIEGT
 * -------------------------------------------------------
 * Den Bauer gab es zweimal, fast wortgleich: im Browser (GuidePanel:
 * layoutAusRezept, richtet Rafaels echtes Fenster her) und im Server (guide.ts,
 * richtet dasselbe Fenster fuer das Pruef-Foto her). Laufen die beiden
 * auseinander, beurteilt das Pruef-Tor ein anderes Fenster als das, das Rafael
 * sieht — und beide Seiten sind fuer sich stimmig, also faellt es niemandem auf.
 * Genau derselbe Grundsatz wie bei den Kachel-Kennungen nebenan.
 *
 * WAS DIESE FASSUNG ZUSAETZLICH KANN: STAPELN (Rafael, 07.09.2026)
 * ----------------------------------------------------------------
 * "Ich will ja keine Sperre. Ich will einfach nur, dass er da untereinander
 * anordnen, nebeneinander und so weiter kann. […] Das passt schon mit dem allein
 * verbleibenden vorhandenen Space sinnvoll, alles nebeneinander zu stapeln."
 *
 * Also: keine Obergrenze, sondern freie Anordnung. Eine Kachel bekommt optional
 * `zeile`; Kacheln mit derselben Zeile stehen nebeneinander, verschiedene Zeilen
 * uebereinander. Der Platz wird zu gleichen Teilen aufgeteilt, solange niemand
 * `breite` vorgibt.
 *
 * WIE FLEXLAYOUT AUSRICHTET — GEMESSEN, NICHT ANGENOMMEN
 * -------------------------------------------------------
 * flexlayout-react 0.8.19, dist/index.js:845-849:
 *
 *     getOrientation() {
 *       if (this.parent === undefined) return isRootOrientationVertical ? VERT : HORZ;
 *       return Orientation.flip(this.parent.getOrientation());
 *     }
 *
 * Die Wurzelreihe ist waagerecht, und JEDE verschachtelte Reihe kippt gegenueber
 * ihrer Elternreihe. Die naheliegende Bauweise — je Zeile eine Reihe direkt in die
 * Wurzel — ergibt deshalb das Gegenteil des Gewuenschten: die "Zeilen" stuenden
 * nebeneinander. Es braucht die Klammer dazwischen:
 *
 *     Wurzelreihe            (waagerecht)
 *       └ Stapel             (senkrecht)   <- die Zeilen untereinander
 *           ├ Zeile 0        (waagerecht)  <- Kacheln nebeneinander
 *           └ Zeile 1        (waagerecht)
 *
 * RUECKWAERTSKOMPATIBILITAET IST DIE PFLICHT, NICHT DIE KUER
 * -----------------------------------------------------------
 * Am 07.09. hatten 129 Karten ein Rezept (1 Kachel: 16, 2: 67, 3: 37, 4: 1, 5: 8).
 * Keines davon nennt `zeile`. Nennt KEINE Kachel eine Zeile, baut diese Funktion
 * deshalb exakt die bisherige eine Reihe — Knoten fuer Knoten dasselbe Dokument.
 * Der Stapel entsteht ausschliesslich dort, wo jemand ihn ausdruecklich anfordert.
 *
 * DIE INDIZES BLEIBEN, WAS SIE WAREN
 * -----------------------------------
 * Kachel-Kennungen tragen den Index der Kachel in der Rezept-Reihenfolge, und das
 * Pruef-Foto findet seine Kacheln ueber genau diesen Index. Gruppiert wird deshalb
 * NUR die Darstellung; jede Kachel behaelt ihren urspruenglichen Index.
 */
import { panelKachelId, sitzungsKachelId } from './guide-kachel-id.js';

/** Die Form, die beide Seiten schon benutzen — hier nur strukturell beschrieben. */
export interface BauKachel {
  art: 'sitzung' | 'panel';
  sessionId?: string;
  accountId?: string;
  component?: string;
  name?: string;
  inhalt?: unknown;
  /** Anteil an der Breite INNERHALB der eigenen Zeile. Ohne Angabe: gleiche Teile. */
  breite?: number;
  /**
   * In welcher Zeile die Kachel steht. Gleiche Zahl = nebeneinander, groessere
   * Zahl = darunter. Ohne Angabe bei ALLEN Kacheln bleibt es bei einer Reihe.
   */
  zeile?: number;
  /**
   * Kacheln DERSELBEN Zeile mit demselben, nicht-leeren `gruppe`-Namen teilen
   * sich EIN Tabset (mehrere Tab-Reiter, ein Fenster) statt je ein eigenes
   * (Rafael, 08.09.2026: Report und Energy fuer denselben Kunden als Tabs statt
   * als zwei Fenster nebeneinander). Ohne Angabe bleibt jede Kachel ihr eigenes
   * Tabset — das bisherige Verhalten, unveraendert.
   */
  gruppe?: string;
}

/**
 * Welche Kachel je Gruppe den Reiter vorn haben soll — die Antwort auf die
 * fehlende `selected`-Angabe (siehe Kopf-Kommentar "WAS DIESE FASSUNG
 * ZUSAETZLICH KANN" fuer den zugehoerigen Befund). Geschwisterkarten teilen
 * sich ab 08.09.2026 dasselbe Rezept (Rafael: "Gemeinsame Fenstermenge, kein
 * Sammeln") — das Rezept selbst sagt deshalb nicht mehr, welche Karte gerade
 * angesehen wird. `seite`/`sitzungen` kommen von der Karte (`grundlage`,
 * `wartet_auf`), nicht aus dem Rezept.
 */
export interface BuehnenAuswahl {
  /** Dateiname (klein geschrieben) der Browser-Seite, die den Vorzug bekommt. */
  seite?: string | null;
  /** Sitzungs-IDs, deren Kachel den Vorzug bekommt — die erste passende gewinnt. */
  sitzungen?: string[];
}

/**
 * Dateiname (klein geschrieben, ohne Pfad/Query) einer Browser-Kachel-Adresse.
 * `null` bei fehlender oder unlesbarer URL. Dieselbe Kennung benutzen das
 * Pruef-Tor (guide.ts, ob die Grundlage im Rezept vorkommt) und die
 * Buehnen-Auswahl hier (welcher Reiter je Gruppe vorn steht) — zwei eigene
 * Fassungen wuerden genau den Drift wiederholen, den der Dateikopf beschreibt.
 */
export function seitenDateiname(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    return decodeURIComponent(new URL(url).pathname).split('/').pop()?.toLowerCase() || null;
  } catch {
    return null;
  }
}

export interface BauErgebnis {
  doc: any;
  /** Kachel-Kennungen OHNE fuehrendes '#' — der Server fotografiert darueber. */
  knoten: string[];
  /** Die Sitzungen, die in der Buehne stehen — der Client oeffnet sie. */
  sitzungen: string[];
}

/** Eine Kachel wird zu ihrem Tab — die Kennung traegt den Index aus dem Rezept. */
function tabFuer(k: BauKachel, i: number): { tab: any; id: string } {
  const id = k.art === 'sitzung'
    ? sitzungsKachelId(k.sessionId!, i)
    : panelKachelId(k.component!, i, k.inhalt ?? {});
  const tab = k.art === 'sitzung'
    ? {
        type: 'tab', id, name: k.name || 'Chat', component: 'cui',
        config: { initialSessionId: k.sessionId, ...(k.accountId ? { accountId: k.accountId } : {}) },
      }
    : {
        // `inhalt` geht unveraendert in die config — dorthin, wo die Panels ihre
        // Angaben lesen (browser: url, business-docs: docId, preview: watchPath).
        type: 'tab', id, name: k.name || k.component, component: k.component,
        config: { ...((k.inhalt as Record<string, unknown>) ?? {}) },
      };
  return { tab, id };
}

/** In welcher Zeile eine Kachel steht — ohne Angabe zaehlt sie zu Zeile 0. */
function zeileVon(k: BauKachel): number {
  return Number.isFinite(k.zeile) ? Number(k.zeile) : 0;
}

/**
 * Gruppiert eine Menge (schon auf eine Zeile eingegrenzt) zu Tabsets: Elemente mit
 * derselben, nicht-leeren `gruppe` teilen sich eins, alle anderen bleiben fuer
 * sich. Von `baueBuehne` UND `fensterAnzahl` benutzt — beide muessen bei "wie
 * viele Fenster entstehen hier" exakt dasselbe zaehlen, sonst urteilt der
 * Kachel-Deckel ueber ein anderes Fenster als das, das am Ende auf dem Bildschirm
 * steht.
 */
function gruppenJeZeile<T>(drin: T[], gruppeVon: (t: T) => string | undefined): T[][] {
  const gruppen: T[][] = [];
  const nachName = new Map<string, T[]>();
  for (const x of drin) {
    const roh = gruppeVon(x);
    const name = roh && String(roh).trim() ? String(roh).trim() : null;
    if (name === null) { gruppen.push([x]); continue; }
    let arr = nachName.get(name);
    if (!arr) { arr = []; nachName.set(name, arr); gruppen.push(arr); }
    arr.push(x);
  }
  return gruppen;
}

/**
 * Wie viele FENSTER (Tabsets) dieses Rezept nebeneinander bzw. untereinander
 * zeigt — das ist die Zahl, die fuer Lesbarkeit zaehlt, nicht die Zahl der
 * Kacheln. Kacheln DERSELBEN Zeile mit derselben `gruppe` teilen sich EIN
 * Fenster mit mehreren Tab-Reitern; die Reiter sind nicht alle gleichzeitig
 * sichtbar (das ist der ganze Sinn eines Reiters), zaehlen also nur einmal.
 *
 * Wird von `rezeptFehler` (guide.ts) als Kachel-Deckel benutzt: zwanzig
 * Browser-Kacheln in EINER `gruppe` sind ein Fenster mit zwanzig Reitern, keine
 * zwanzig Fenster — der Deckel greift erst, wenn wirklich viele Fenster
 * gleichzeitig auf dem Bildschirm stehen wuerden.
 */
export function fensterAnzahl(kacheln: BauKachel[]): number {
  const zeilenNummern = new Set(kacheln.map(zeileVon));
  let summe = 0;
  for (const z of zeilenNummern) {
    summe += gruppenJeZeile(kacheln.filter(k => zeileVon(k) === z), k => k.gruppe).length;
  }
  return summe;
}

/** Ob diese Kachel laut `auswahl` in ihrer Gruppe vorn stehen soll. */
function passtZurAuswahl(k: BauKachel, auswahl: BuehnenAuswahl): boolean {
  if (k.art === 'sitzung') return !!k.sessionId && !!auswahl.sitzungen?.includes(k.sessionId);
  if (k.art === 'panel' && k.component === 'browser') {
    const datei = seitenDateiname((k.inhalt as Record<string, unknown> | undefined)?.url);
    return !!datei && !!auswahl.seite && datei === auswahl.seite;
  }
  return false;
}

export function baueBuehne(kacheln: BauKachel[], alt: any, auswahl?: BuehnenAuswahl): BauErgebnis {
  const knoten: string[] = [];

  /*
   * Zeilen bilden. `zeile` wird als Gruppierungsschluessel benutzt, nicht als
   * Position: schreibt jemand 0 und 7, sind das zwei Zeilen, keine acht. So kann
   * eine Zeile entfallen, ohne dass eine leere Luecke im Fenster steht.
   */
  const gestapelt = kacheln.some(k => Number.isFinite(k.zeile));
  const schluessel = zeileVon;
  const zeilenNummern = [...new Set(kacheln.map(schluessel))].sort((a, b) => a - b);

  // Index MIT der Kachel fuehren — er ueberlebt das Gruppieren und bleibt der
  // Index aus dem Rezept, an dem das Pruef-Foto seine Kacheln wiederfindet.
  const mitIndex = kacheln.map((k, i) => ({ k, i }));

  /*
   * GRUPPIEREN INNERHALB EINER ZEILE (Rafael, 08.09.2026)
   * ------------------------------------------------------
   * Kacheln mit derselben, nicht-leeren `gruppe` teilen sich ab jetzt EIN
   * Tabset (mehrere Tab-Reiter, ein Fenster) statt je ein eigenes. Ohne
   * `gruppe` bleibt jede Kachel eine eigene, einelementige Gruppe — exakt das
   * bisherige Verhalten (Rueckwaertskompatibilitaets-Pflicht siehe Kopf).
   *
   * Dieselbe Gruppierung braucht `fensterAnzahl` (weiter unten) fuer den
   * Kachel-Deckel — deshalb `gruppenJeZeile`, nicht hier zweimal von Hand gebaut.
   */
  const gruppieren = (drin: { k: BauKachel; i: number }[]) => gruppenJeZeile(drin, x => x.k.gruppe);

  const tabsetFuerGruppe = (gruppe: { k: BauKachel; i: number }[], gruppenInZeile: number) => {
    const tabs = gruppe.map(({ k, i }) => {
      const { tab, id } = tabFuer(k, i);
      knoten.push(id.slice(1));
      return tab;
    });
    const breite = gruppe.map(({ k }) => k.breite).find(b => b && b > 0);
    /*
     * WELCHER REITER VORN STEHT (Rafael, 08.09.2026 — "Gemeinsame Fenstermenge,
     * kein Sammeln").
     *
     * flexlayout zeigt ohne `selected` immer den ERSTEN Kind-Tab eines Tabsets.
     * Solange Geschwisterkarten noch je ihr eigenes Rezept schrieben, wurde das
     * ueber die Reihenfolge der Kacheln umgangen (die eigene Seite/der eigene
     * Chat kam im eigenen Rezept zuerst) — genau das verhinderte aber, dass zwei
     * Karten dasselbe Rezept (und damit denselben Fingerabdruck) tragen durften.
     * Mit `auswahl` bleibt das Rezept identisch; nur der Reiter, der beim
     * OEFFNEN dieser einen Karte vorn liegt, kommt aus der Karte selbst
     * (`grundlage`, `wartet_auf`) statt aus der Kachel-Reihenfolge.
     *
     * Kein Treffer in der Gruppe -> kein `selected` gesetzt -> flexlayout faellt
     * auf den ersten Reiter zurueck, exakt das bisherige Verhalten (auch ohne
     * `auswahl`-Argument, z.B. bei der Buehnenvorschau ohne Kartenbezug).
     */
    const ausgewaehlt = auswahl ? gruppe.findIndex(({ k }) => passtZurAuswahl(k, auswahl)) : -1;
    return {
      type: 'tabset', id: `#guide-ts-${gruppe[0].i}`,
      weight: breite ?? Math.round(100 / Math.max(1, gruppenInZeile)),
      children: tabs,
      ...(gruppe.some(({ i }) => i === 0) ? { active: true } : {}),
      ...(ausgewaehlt >= 0 ? { selected: ausgewaehlt } : {}),
    };
  };

  let layout: any;
  if (!gestapelt || zeilenNummern.length <= 1) {
    // GENAU wie bisher: eine Reihe, links nach rechts (bzw. gruppierte Tabsets darin).
    const gruppen = gruppieren(mitIndex);
    layout = { type: 'row', id: '#guide-row', children: gruppen.map(g => tabsetFuerGruppe(g, gruppen.length)) };
  } else {
    const zeilen = zeilenNummern.map(z => {
      const drin = mitIndex.filter(x => schluessel(x.k) === z);
      const gruppen = gruppieren(drin);
      return {
        type: 'row', id: `#guide-zeile-${z}`,
        weight: Math.round(100 / zeilenNummern.length),
        children: gruppen.map(g => tabsetFuerGruppe(g, gruppen.length)),
      };
    });
    // Die Klammer, die aus nebeneinander ein untereinander macht (siehe Kopf).
    layout = {
      type: 'row', id: '#guide-row',
      children: [{ type: 'row', id: '#guide-stapel', weight: 100, children: zeilen }],
    };
  }

  return {
    doc: {
      global: alt?.global ?? { splitterSize: 4, tabEnableRenderOnDemand: false },
      borders: [],
      layout,
      popouts: {},
      // Die aktuelle Fassung mitschicken: ein kleinerer Wert wird mit 409 abgewiesen.
      ...(typeof alt?._v === 'number' ? { _v: alt._v } : {}),
    },
    knoten,
    sitzungen: kacheln.filter(k => k.art === 'sitzung').map(k => k.sessionId!),
  };
}
